import { Upload, Link as LinkIcon, Info } from 'lucide-react';
import { useState, FormEvent } from 'react';

interface ResumeUploadFormProps {
  onSubmit: (formData: {
    file: File;
    jobUrl: string;
    jobDescription: string;
    isUrlMode: boolean;
    enhanceCV: boolean;
  }) => Promise<void>;
  isProcessing: boolean;
  remainingResumes: number;
}

export function ResumeUploadForm({ onSubmit, isProcessing, remainingResumes }: ResumeUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [vacancyUrl, setVacancyUrl] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [isUrlMode, setIsUrlMode] = useState(false);
  const [enhanceCV, setEnhanceCV] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const isFormValid = () => {
    if (!file || remainingResumes <= 0) return false;
    return isUrlMode ? !!vacancyUrl : !!jobDescription;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isFormValid() || !file) return;

    await onSubmit({
      file,
      jobUrl: vacancyUrl,
      jobDescription,
      isUrlMode,
      enhanceCV
    });

    // Reset form
    setFile(null);
    setVacancyUrl('');
    setJobDescription('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 space-y-6">
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Загрузите ваше резюме
          </label>
          <div className="relative">
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.docx"
              className="hidden"
              id="cv-upload"
              disabled={remainingResumes <= 0}
            />
            <label
              htmlFor="cv-upload"
              className={`flex items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-md appearance-none ${
                remainingResumes > 0 ? 'cursor-pointer hover:border-blue-500' : 'cursor-not-allowed opacity-50'
              } focus:outline-none`}
            >
              <div className="flex flex-col items-center space-y-2">
                <Upload className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-center text-gray-500">
                  {file ? file.name : 'Нажмите, чтобы загрузить резюме'}
                </span>
                <span className="text-xs text-gray-500">
                  PDF, Word (.docx)
                </span>
              </div>
            </label>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Детали вакансии
            </label>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Текст</span>
              <button
                type="button"
                onClick={() => setIsUrlMode(!isUrlMode)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  isUrlMode ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isUrlMode ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-500">URL</span>
            </div>
          </div>

          {isUrlMode && (
            <div className="mb-4 bg-amber-50 border-l-4 border-amber-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Info className="h-5 w-5 text-amber-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-700">
                    Для лучших результатов рекомендуется скопировать и вставить текст описания вакансии напрямую. Некоторые сайты могут блокировать доступ к своему контенту.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isUrlMode ? (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LinkIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                value={vacancyUrl}
                onChange={(e) => setVacancyUrl(e.target.value)}
                placeholder="Вставьте URL вакансии"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          ) : (
            <div className="relative">
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Вставьте описание вакансии"
                rows={6}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {/* CV Enhancement Toggle */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center space-x-2">
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={enhanceCV}
                    onChange={(e) => setEnhanceCV(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                    enhanceCV ? 'bg-blue-600' : 'bg-gray-200'
                  }`}>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
                      enhanceCV ? 'transform translate-x-4' : ''
                    }`} />
                  </div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Приукрасить резюме
                </span>
              </label>
              <div className="relative group">
                <Info className="h-5 w-5 text-gray-400 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-72 bg-gray-900 text-white text-sm rounded-lg p-3 shadow-lg">
                  <div className="relative">
                    <div className="absolute w-3 h-3 bg-gray-900 transform rotate-45 left-2 -bottom-1"></div>
                    <p>
                      При включении этой опции, AI может добавить в ваше резюме релевантные навыки и инструменты, которые не были указаны в оригинальном резюме, но соответствуют требованиям вакансии.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={!isFormValid() || isProcessing}
            className={`w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white ${
              !isFormValid() || isProcessing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
          >
            {isProcessing ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Обработка...</span>
              </div>
            ) : (
              <span>Создать резюме</span>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}